// Section 19: executing a season's draw (stream E2, ADR-0016). Candidates are taken in order of
// how decisively they were drawn (roll / chance), thinned by the event's `weight(ctx)` for the
// current state (an event needing a literate adult is dropped without one; arrivals rise above
// trust 75), and capped at `maxPerSeason`. An event fires when roll < chance x w / (base x cap),
// so its overall chance is w x season bias x options: the draws never change, only their effects.
import { clamp } from '../../core/math';
import { createRng, hashString } from '../../core/rng';
import type {
  EventCandidate,
  EventDraw,
  EventInstance,
  PhaseContext,
  ReadContext,
} from '../../core/types';
import { applyDeltas } from '../shared/deltas';
import { weightCap } from './deck';

/** A context whose event stream is the instance's own, so effects never shift the deck. */
export function scoped(ctx: PhaseContext, seed: number, salt: string): PhaseContext {
  return { ...ctx, eventRng: createRng((seed ^ hashString(salt)) >>> 0).rng };
}

function instanceOf(c: EventCandidate, turn: number): EventInstance {
  return { eventId: c.eventId, turn, seed: c.seed };
}

/** The events that strike this season, in firing order (at most `maxPerSeason`). */
export function firedEvents(ctx: PhaseContext, draw: EventDraw): EventCandidate[] {
  const ordered = [...draw.candidates].sort(
    (a, b) => a.roll / a.chance - b.roll / b.chance || (a.eventId < b.eventId ? -1 : 1),
  );
  const fired: EventCandidate[] = [];
  for (const c of ordered) {
    if (fired.length >= ctx.cfg.eventDeck.maxPerSeason) break;
    const def = ctx.registry.events[c.eventId];
    const hctx: ReadContext = { ...ctx.live, event: instanceOf(c, draw.turn) };
    const weight = Math.max(0, def.weight(hctx));
    const accept =
      def.baseWeight > 0
        ? clamp(weight / (def.baseWeight * weightCap(ctx.cfg, c.eventId)), 0, 1)
        : 0;
    if (c.roll < c.chance * accept) fired.push(c);
  }
  return fired;
}

/** Run one event: its headline entry, then its immediate deltas (effects, people, a prompt). */
export function runEvent(ctx: PhaseContext, c: EventCandidate, turn: number): void {
  const def = ctx.registry.events[c.eventId];
  const hctx: ReadContext = { ...ctx.live, event: instanceOf(c, turn) };
  ctx.report.add(def.text(hctx));
  applyDeltas(scoped(ctx, c.seed, 'apply'), def.apply(hctx));
}
